import { useEffect, useRef } from 'react'
import { useQuery } from '@apollo/client/react'
import { toast } from 'sonner'
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react'
import { PortalBillingPaymentStatusDocument } from '@/graphql/generated/graphql'

export interface PaymentLinkData {
  id: string
  url: string
  qr?: string | null
  status: string
  expiresAt?: string | null
}

interface PaymentLinkPanelProps {
  billingId: number
  /** Total REAL de la factura (devuelto por el backend; puede haber prorrateo). */
  totalReal: number
  currencyName: string
  link?: PaymentLinkData | null
  /** Causa del fallo al generar el link (el negocio/factura ya existen). */
  linkError?: string | null
  retrying?: boolean
  onRetry: () => void
  /** La factura quedó PAID (el backend registró el pago automáticamente). */
  onPaid: () => void
}

function isExpired(link: PaymentLinkData): boolean {
  return !!link.expiresAt && new Date(link.expiresAt).getTime() < Date.now()
}

/**
 * Pantalla de pago por link (pagos-zeus-3): total real + "Pagar ahora" + QR +
 * copiar. Consulta el estado de la factura hasta verla PAID — el cobro llega
 * solo por el canal interno de Midas y Zeus activa el negocio.
 */
export function PaymentLinkPanel({
  billingId,
  totalReal,
  currencyName,
  link,
  linkError,
  retrying,
  onRetry,
  onPaid,
}: PaymentLinkPanelProps) {
  const { data, stopPolling } = useQuery(PortalBillingPaymentStatusDocument, {
    variables: { billingId },
    skip: !billingId,
    pollInterval: 5000,
    fetchPolicy: 'network-only',
  })

  // `paid` se deriva del estado real de la factura; onPaid se dispara una vez.
  const paid = data?.portalBilling?.status === 'PAID'
  const paidNotified = useRef(false)
  useEffect(() => {
    if (paid && !paidNotified.current) {
      paidNotified.current = true
      stopPolling()
      onPaid()
    }
  }, [paid, stopPolling, onPaid])

  const copyLink = async () => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link.url)
      toast.success('Link de pago copiado')
    } catch {
      toast.error('No se pudo copiar el link')
    }
  }

  if (paid) {
    return (
      <div className="rounded-2xl bg-emerald-50 p-5 text-center" role="status">
        <CheckCircle2 className="mx-auto size-10 text-emerald-600" />
        <p className="mt-2 text-sm font-bold text-emerald-700">
          ¡Pago confirmado!
        </p>
        <p className="text-xs text-emerald-700/80">
          Tu factura quedó pagada y los servicios se están activando.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-2xl bg-neutral-100 px-4 py-3">
        <span className="text-sm font-semibold">Total a pagar</span>
        <span className="text-base font-bold text-primary">
          {totalReal.toFixed(2)} {currencyName}
        </span>
      </div>

      {linkError && (
        <div className="rounded-2xl bg-amber-50 p-4">
          <p className="flex items-start gap-2 text-sm text-amber-800">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            {linkError}
          </p>
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background hover:bg-primary disabled:opacity-40"
          >
            {retrying ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            Reintentar link de pago
          </button>
        </div>
      )}

      {link && (
        <>
          {isExpired(link) ? (
            <div className="rounded-2xl bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                El link de pago expiró. Genera uno nuevo para continuar.
              </p>
              <button
                type="button"
                onClick={onRetry}
                disabled={retrying}
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-foreground px-4 py-2 text-sm font-semibold text-background hover:bg-primary disabled:opacity-40"
              >
                {retrying ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
                Regenerar link
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {link.qr && (
                <div className="grid place-items-center">
                  <img
                    src={
                      link.qr.startsWith('data:')
                        ? link.qr
                        : `data:image/png;base64,${link.qr}`
                    }
                    alt="Código QR del link de pago"
                    className="size-44 rounded-2xl border border-black/10 bg-white p-2"
                  />
                </div>
              )}
              <div className="flex flex-wrap items-center justify-center gap-3">
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-sm font-semibold text-background hover:bg-primary"
                >
                  <ExternalLink className="size-4" /> Pagar ahora
                </a>
                <button
                  type="button"
                  onClick={() => void copyLink()}
                  className="inline-flex items-center gap-2 rounded-xl border border-black/10 px-4 py-2.5 text-sm font-semibold hover:border-black/25"
                >
                  <Copy className="size-4" /> Copiar link
                </button>
              </div>
              <p
                className="flex items-center justify-center gap-2 text-xs text-muted-foreground"
                role="status"
              >
                <Loader2 className="size-3.5 animate-spin" />
                Esperando la confirmación del pago…
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
